/**
 * 크롤링 작업 관련 Context API
 * 
 * 이 모듈은 애플리케이션 전체에서 크롤링 작업 데이터와 관련 함수를 공유하기 위한
 * React Context를 제공합니다. 이를 통해 컴포넌트 트리 깊숙이 props를 전달하지 않고도
 * 크롤링 작업 관련 데이터와 기능에 접근할 수 있습니다.
 */

import React, { createContext, useContext } from 'react';
import useCrawlJobs from '../hooks/useCrawlJobs';

// 크롤링 작업 Context 생성
const CrawlContext = createContext();

/**
 * 크롤링 Context Provider 컴포넌트
 * 
 * 이 컴포넌트는 useCrawlJobs 훅의 결과를 Context에 제공합니다.
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Provider 내부에 렌더링될 자식 컴포넌트
 */
export function CrawlProvider({ children }) {
  const crawl = useCrawlJobs();
  return (
    <CrawlContext.Provider value={crawl}>
      {children}
    </CrawlContext.Provider>
  );
}

/**
 * 크롤링 Context 사용을 위한 커스텀 훅
 * 
 * 이 훅을 사용하여 컴포넌트에서 크롤링 작업 데이터와 함수에 접근할 수 있습니다.
 * 
 * @returns {Object} 크롤링 작업 상태와 관련 함수를 포함하는 객체
 * @example
 * function MyComponent() {
 *   const { jobs, loading, addJob } = useCrawlContext();
 *   // jobs 데이터 사용 또는 addJob 함수 호출
 * }
 */
export function useCrawlContext() {
  return useContext(CrawlContext);
} 